import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-empty-state>` says about itself.
 *
 * ── ON `title` ───────────────────────────────────────────────────────────────
 * The heading is set through the `title` attribute — which is also a global HTML attribute, so the
 * browser will show the same string as a native tooltip when the pointer rests on the region. That
 * is a quirk of the name the component chose, not something an author sets a second time, and it is
 * worth knowing before somebody writes a long sentence into it.
 *
 * ── WHAT MAKES A GOOD ONE ────────────────────────────────────────────────────
 * The two examples are two different empties, and they are two examples because saying the wrong
 * one is a real failure: "there is nothing yet" and "nothing matches what you asked for" look
 * identical and need opposite next steps. The first wants the thing that creates a record; the
 * second wants the filter cleared.
 */
const docs: ComponentDocs = {
  aliases: ['empty state', 'blank slate', 'no results', 'zero state', 'nothing here', 'placeholder screen'],

  answers: [
    'explain why a screen is empty',
    'say what would put something here',
    'give somebody the one thing to do next when there is nothing to look at',
    'tell somebody their filter matched nothing',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'title',
      attribute: 'title',
      type: 'text',
      description:
        'The heading — one short line naming what is missing ("No orders yet"), not a sentence. ' +
        'Empty or unset and the heading is not drawn at all. Note this is also the global HTML title ' +
        'attribute, so the browser shows it as a tooltip too.',
    },
  ],

  // Declared as NONE rather than left unsaid: the piece is a statement, and anything that can be
  // ACTED on here is a control the author puts in the action slot, which raises its own events.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The description under the heading: a sentence or two saying what would put something here. ' +
        'This is the half that does the work — a heading alone only restates the blank region.',
    },
    {
      name: 'icon',
      takes:
        'A glyph above the heading, replacing the default. Left alone it draws a neutral inbox mark, ' +
        'sized 40px and drawn in the muted ink. Hidden from assistive technology either way.',
    },
    {
      name: 'action',
      takes:
        'The one thing to do next — usually a single Button. The row is not drawn at all when the ' +
        'slot is empty, so an empty state with nothing to offer does not leave a gap where a control ' +
        'would be.',
    },
  ],

  tokens: [
    { token: '--caos-color-text', controls: 'The heading ink.' },
    { token: '--caos-color-text-muted', controls: 'The icon and the description ink.' },
    { token: '--caos-font-display', controls: 'The face of the heading.' },
    { token: '--caos-space-3', controls: 'The gaps between the icon, the heading and the description.' },
    { token: '--caos-space-2', controls: 'The gap between actions, and the space above the action row.' },
    { token: '--caos-space-5', controls: 'The padding across the column.' },
    { token: '--caos-space-7', controls: 'The padding above and below — what gives the state its room.' },
  ],

  parts: [
    { part: 'container', is: 'the centred block' },
    { part: 'icon', is: 'the glyph above the title' },
    { part: 'title', is: 'the headline' },
    { part: 'description', is: 'the explanatory text' },
    { part: 'actions', is: 'the row holding the slotted action' },
  ],

  methods: [],

  placing:
    'It goes WHERE THE CONTENT WOULD HAVE GONE, filling that region rather than sitting above or ' +
    'below it: a centred column capped at 420px wide, so it stays readable in a region of any ' +
    'width. Some pieces place it for you — DataTable builds one from its empty-label when it has no ' +
    'rows, and takes a replacement in its empty slot.',

  whenToUse:
    'A region has nothing to show and would otherwise be blank. It is never optional: a bare empty ' +
    'area reads as a screen that has broken or not finished, and both of those are wrong more often ' +
    'than they are right.',

  insteadUse: [
    {
      name: 'SkeletonBlock',
      when: 'The region is not empty — it is still loading, and something is on its way.',
    },
    {
      name: 'ErrorPanel',
      when: 'The region is empty because something FAILED. That needs the code, the correlation id and a way forward.',
    },
    {
      name: 'AccessDeniedPanel',
      when:
        'The region is empty because this person may not see what is in it. That has to say which ' +
        'grant is missing without leaking what was hidden — an empty state would be a lie.',
    },
    {
      name: 'NoticeBanner',
      when:
        'There IS content, and something needs saying about it. A notice sits above the content; an ' +
        'empty state stands in for it.',
    },
  ],

  accessibility:
    'The icon is hidden from assistive technology, so nothing meaningless is announced, and the ' +
    'heading and description are read in order as ordinary text. What is left to the author is the ' +
    'wording, and it is the whole job: the heading names what is missing, the description says what ' +
    'would put something here, and the action is the one thing to do about it. The piece draws no ' +
    'heading level of its own, so a surface that needs this in its heading outline has to supply ' +
    'one. It is also not a live region — replacing loaded content with an empty state after a ' +
    'filter change announces nothing unless the region around it says so.',

  examples: [
    {
      key: 'nothing-yet',
      label: 'Nothing yet',
      summary:
        'The list has never had anything in it: the heading names what is missing, the description ' +
        'says what would put something here, and the action is the thing that does it.',
      placement: {
        component: 'caos-empty-state',
        inputs: { title: 'No orders yet' },
        children: [
          'Orders raised for this account will be listed here, newest first. Start one from a ' +
            'request, or from a blank order.',
          { component: 'caos-button', inputs: { slot: 'action', variant: 'primary' }, children: ['New order'] },
        ],
      },
    },
    {
      key: 'no-match',
      label: 'Nothing matches',
      summary:
        'The same region, empty for the opposite reason: there ARE orders, the filter just excluded ' +
        'all of them — so the way out is to widen the filter, not to create a record.',
      placement: {
        component: 'caos-empty-state',
        inputs: { title: 'No orders match this filter' },
        children: [
          'Twelve orders exist on this account, but none of them reached the Approved stage in the ' +
            'last 30 days.',
          { component: 'caos-button', inputs: { slot: 'action', variant: 'secondary' }, children: ['Clear filters'] },
        ],
      },
    },
  ],
};

export default docs;
